import React from "react";
import { Flex, HStack, Text, Icon } from "@chakra-ui/react";
import { HiOutlineSparkles, HiOutlineArrowRight } from "react-icons/hi2";
import { c, glassSurface } from "../../classroomColors";
import { brand, onTint } from "../../standeTokens";
import { waitedMinutes } from "../chat/clusterQuestions";

/**
 * What to cover next, read off the room rather than guessed (wireframe
 * 130:54, the pill above the canvas).
 *
 * The suggestion is derived, not generated: the biggest group of students
 * asking the same thing is what a class most needs next, and saying so in
 * their words is both the most useful phrasing and the most honest one.
 * Clicking it opens that group.
 */
export const focusSuggestion = (clusters, now = Date.now()) => {
  if (!clusters.length) return null;
  const top = clusters[0];
  const minutes = waitedMinutes(top, now);

  if (top.count >= 3) {
    return {
      cluster: top,
      urgency: "high",
      text: `${top.count} students are stuck on the same thing — “${top.headline}”`,
    };
  }
  if (minutes >= 4) {
    return {
      cluster: top,
      urgency: "high",
      text: `“${top.headline}” has been waiting ${minutes} minutes`,
    };
  }
  return {
    cluster: top,
    urgency: "normal",
    text: `Worth covering next: “${top.headline}”`,
  };
};

const FocusBanner = ({ clusters, now, isDark, onOpenCluster }) => {
  const b = brand(isDark);
  const tint = onTint(isDark);
  const suggestion = focusSuggestion(clusters, now);
  const high = suggestion?.urgency === "high";

  return (
    <Flex
      as={suggestion ? "button" : "div"}
      type={suggestion ? "button" : undefined}
      onClick={suggestion ? () => onOpenCluster?.(suggestion.cluster) : undefined}
      align="center"
      gap="10px"
      px="14px"
      py="9px"
      borderRadius="200px"
      textAlign="left"
      maxW="100%"
      {...(high
        ? { bg: b.lightRed, border: "1px solid", borderColor: "transparent" }
        : glassSurface)}
      transition="background 0.15s"
      _hover={suggestion ? { bg: high ? b.lightRed : c.hover, opacity: high ? 0.9 : 1 } : {}}
    >
      <Flex
        flexShrink={0}
        boxSize="22px"
        borderRadius="full"
        align="center"
        justify="center"
        bg={high ? "rgba(255,255,255,0.65)" : c.surfaceSubtle}
      >
        <Icon as={HiOutlineSparkles} boxSize="13px" color={high ? tint.lightRed : c.textSub} />
      </Flex>

      <Text
        fontSize="13px"
        color={high ? tint.lightRed : c.textSub}
        fontWeight={high ? "500" : "400"}
        noOfLines={1}
        minW={0}
      >
        {suggestion ? suggestion.text : "What you can focus on next will appear here as students ask questions"}
      </Text>

      {suggestion && (
        <HStack spacing="4px" flexShrink={0} color={high ? tint.lightRed : c.textMuted}>
          <Text fontSize="11.5px" fontWeight="500">Open</Text>
          <Icon as={HiOutlineArrowRight} boxSize="12px" />
        </HStack>
      )}
    </Flex>
  );
};

export default FocusBanner;
